import { useEffect, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { cn } from '@/shared/lib/cn';

const HEX_RE = /^#([0-9A-Fa-f]{6})$/;

function normalizeHex(value: string): string {
  let next = value.trim();
  if (!next.startsWith('#')) next = `#${next}`;
  return next.slice(0, 7);
}

type ColorPickerProps = {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** compact = swatch quadrado (ex.: perfil NPS); default = botão com hex */
  size?: 'default' | 'compact';
};

export function ColorPicker({
  value,
  onChange,
  disabled,
  className,
  id,
  size = 'default',
}: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(value);

  useEffect(() => {
    setHex(normalizeHex(value));
  }, [value]);

  const displayColor = HEX_RE.test(hex) ? hex : HEX_RE.test(value) ? value : '#000000';

  const commit = (next: string) => {
    const normalized = normalizeHex(next);
    setHex(normalized);
    if (HEX_RE.test(normalized)) onChange(normalized);
  };

  const swatch = (
    <span
      className={cn(
        'shrink-0 rounded-md border border-border shadow-sm',
        size === 'compact' ? 'size-full' : 'size-5',
      )}
      style={{ backgroundColor: displayColor }}
    />
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={`Cor ${displayColor}`}
          className={cn(
            size === 'compact'
              ? 'size-10 shrink-0 p-1.5'
              : 'h-10 w-full justify-start gap-3 px-3 font-normal',
            className,
          )}
        >
          {swatch}
          {size === 'default' ? (
            <span className="truncate font-mono text-sm uppercase">{displayColor}</span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align={size === 'compact' ? 'end' : 'start'}>
        <div className="space-y-3">
          <HexColorPicker
            color={displayColor}
            onChange={commit}
            className="!w-full [&_.react-colorful__pointer]:size-4 [&_.react-colorful__saturation]:rounded-lg [&_.react-colorful__hue]:mt-3 [&_.react-colorful__hue]:rounded-full"
          />
          <Input
            value={hex}
            onChange={(e) => commit(e.target.value)}
            maxLength={7}
            placeholder="#000000"
            className="font-mono uppercase"
            aria-label="Valor hexadecimal"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
